<script lang="ts">
// Tipe nama ikon diekspor ulang agar pemanggil lama tetap bisa menulis
// `import AppIcon, { type IconName } from '@/components/AppIcon.vue'`.
export type { IconName } from '@/lib/ikon'
</script>

<script setup lang="ts">
// Ikon produk memakai **Material Symbols (Material Design 3)**, gaya Rounded.
//
// Nama ikon di seluruh aplikasi TIDAK berubah (`<AppIcon name="calendar" />`
// tetap sah) — komponen inilah yang memetakannya ke glyph Material. Dengan
// begitu peralihan dari set SVG buatan sendiri ke M3 cukup dikerjakan di satu
// berkas, dan ratusan pemanggilan di halaman lain tidak perlu disentuh.
//
// Fontnya di-subset lewat `icon_names` pada index.html: hanya glyph yang
// benar-benar dipakai yang diunduh.
import { computed } from 'vue'
import { GLYPH, type IconName } from '@/lib/ikon'

const props = defineProps<{ name: IconName; class?: string; isi?: boolean }>()

// Ukuran glyph mengikuti kelas tinggi Tailwind yang sudah dipakai pemanggil
// (h-4 → 1rem, h-5 → 1.25rem, dst.), sehingga peralihan ini tidak mengubah
// tata letak satu halaman pun.
const ukuran = computed(() => {
  const cocok = /h-(\d+(?:\.\d+)?)/.exec(props.class ?? '')
  const n = cocok ? Number(cocok[1]) : 5
  return `${(n / 4).toFixed(3)}rem`
})

// Sumbu variabel Material Symbols: FILL untuk ikon terisi, opsz mengikuti
// ukuran agar ketebalan garisnya pas pada tiap ukuran.
const gaya = computed(() => ({
  fontSize: ukuran.value,
  fontVariationSettings: `'FILL' ${props.isi ? 1 : 0}, 'wght' 450, 'GRAD' 0, 'opsz' 24`,
}))
</script>

<template>
  <span :class="['ikon-m3', props.class ?? 'h-5 w-5']" :style="gaya" aria-hidden="true">{{ GLYPH[name] }}</span>
</template>

<style>
/* Global (bukan scoped) supaya kelas ukuran dari pemanggil tetap berlaku. */
.ikon-m3 {
  font-family: 'Material Symbols Rounded';
  font-weight: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  direction: ltr;
  -webkit-font-feature-settings: 'liga';
  font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
  user-select: none;
  flex-shrink: 0;
}
</style>
